import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { bN, diaCurto, hojeBrasilia, iniciais, lerEquipe, simular } from '../../giro';
import type { BlocoDaOperacao, PessoaDaEquipe, PlanoDoDia, SetorDoGiro } from '../../giro';
import { podeSoltarNoBloco, podeTransferirBloco } from '../../giro-gestor';
import { Barra, Selo, Transferencia, useOperacao } from './Operacao';

/**
 * QUADRO DE BLOCOS (Setores & blocos, abaixo da tabela): o setor escolhido com os blocos do dia e
 * quem está em cada um.
 *
 *   - com a operação do dia publicada: arrastar a pessoa (da coluna da equipe ou de outro bloco)
 *     para um bloco = passar o bloco para ela, ou TROCAR se ela já tem bloco aqui (X ↔ Y) — com
 *     motivo e registro. BLOCOS FIXOS, BALCONISTAS TROCAM (dono, 27/09/2026): só com os blocos
 *     NÃO COMEÇADOS (os dois, na troca). No celular, "Trocar dono" no cartão faz o mesmo.
 *   - sem operação ainda: a PRÉVIA da simulação (não grava nada, não gira o rodízio) mostra os
 *     blocos e quem o rodízio colocaria em cada um. Escolher o dono ANTES de gerar ainda não existe:
 *     gera e arrasta.
 */
export function QuadroDeBlocos({
  sessao,
  setores,
  setorId,
  aoEscolherSetor,
}: {
  sessao: Sessao;
  setores: readonly SetorDoGiro[];
  setorId: string | null;
  aoEscolherSetor: (id: string) => void;
}) {
  const dia = hojeBrasilia();
  const { d: op, erro, carregar } = useOperacao(dia);
  const ativos = setores.filter((s) => s.ativo);
  const atual = ativos.find((s) => s.id === setorId) ?? ativos[0] ?? null;
  const [pessoas, setPessoas] = useState<readonly PessoaDaEquipe[] | null>(null);
  const [previa, setPrevia] = useState<PlanoDoDia | null>(null);
  const [simulando, setSimulando] = useState(false);
  const [falhaPrevia, setFalhaPrevia] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState<PessoaDaEquipe | null>(null);
  const [sobre, setSobre] = useState<number | null>(null);
  const [transferindo, setTransferindo] = useState<{
    bloco: BlocoDaOperacao;
    para: string;
  } | null>(null);

  const idAtual = atual?.id ?? null;
  const lerPessoas = useCallback(() => {
    if (!idAtual) return;
    void lerEquipe(dia, idAtual).then((r) => r.ok && setPessoas(r.dados.pessoas));
  }, [dia, idAtual]);
  useEffect(() => {
    setPessoas(null);
    setPrevia(null);
    setFalhaPrevia(null);
    lerPessoas();
  }, [lerPessoas]);

  if (!atual) return null;
  const doSetor = op?.setores.find((s) => s.setorId === atual.id) ?? null;
  const geracao =
    doSetor?.geracao && doSetor.geracao.estado !== 'CANCELADA' ? doSetor.geracao : null;
  const blocos = geracao ? (doSetor?.blocos ?? []) : [];
  const publicada = geracao?.estado === 'PUBLICADA';

  const verPrevia = () => {
    setSimulando(true);
    setFalhaPrevia(null);
    void simular(sessao, atual.id, dia).then((r) => {
      setSimulando(false);
      if (r.tipo === 'confirmada') setPrevia(r.dados);
      else
        setFalhaPrevia(
          r.tipo === 'recusada' ? r.mensagem : 'Sem resposta do servidor. Tente de novo.',
        );
    });
  };
  const soltar = (b: BlocoDaOperacao) => {
    setSobre(null);
    if (arrastando && podeSoltarNoBloco(b, arrastando, blocos))
      setTransferindo({ bloco: b, para: arrastando.accountId });
    setArrastando(null);
  };
  const pegar = (accountId: string | undefined) =>
    setArrastando(pessoas?.find((p) => p.accountId === accountId) ?? null);

  return (
    <div className="cv-cartao gq">
      <div className="gq-topo">
        <div>
          <span className="gr-rotulo">BLOCOS DE HOJE · {diaCurto(dia)}</span>
          <h2>{atual.nome}</h2>
        </div>
        <div className="gq-setores" role="tablist" aria-label="Setor">
          {ativos.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={s.id === atual.id}
              className={`gq-chip ${s.id === atual.id ? 'gq-chip-ativo' : ''}`}
              onClick={() => aoEscolherSetor(s.id)}
            >
              {s.nome}
            </button>
          ))}
        </div>
      </div>

      {erro ? (
        <ErroDeArea oQue="a operação de hoje" aoTentar={carregar} />
      ) : !op ? (
        <div className="cv-carregando" aria-busy="true" />
      ) : (
        <div className="gq-corpo">
          <aside className="gq-equipe" aria-label="Equipe do setor">
            <span className="gr-rotulo">EQUIPE DO SETOR · {pessoas?.length ?? '—'}</span>
            {pessoas && !pessoas.length && (
              <p className="cv-cinza cv-peq">Ninguém vinculado. Vincule no editor do setor.</p>
            )}
            <ul>
              {pessoas?.map((p) => {
                const folga = p.presenca === 'AUSENTE';
                return (
                  <li
                    key={p.accountId}
                    className={`gq-pessoa ${folga ? 'gq-folga' : ''}`}
                    draggable={publicada && !folga}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', p.accountId);
                      e.dataTransfer.effectAllowed = 'move';
                      setArrastando(p);
                    }}
                    onDragEnd={() => {
                      setArrastando(null);
                      setSobre(null);
                    }}
                    title={publicada && !folga ? 'Arraste para um bloco' : undefined}
                  >
                    <span className="gq-avatar">{iniciais(p.nome)}</span>
                    <span className="gq-nome">{p.nome}</span>
                    <em>
                      {folga
                        ? 'folga'
                        : p.blocosHoje.length
                          ? p.blocosHoje.map((b) => bN(b.numero)).join(' · ')
                          : 'sem bloco'}
                    </em>
                  </li>
                );
              })}
            </ul>
          </aside>

          <div className="gq-blocos">
            {geracao ? (
              <>
                <p className="cv-cinza cv-peq gq-dica">
                  {publicada ? (
                    <>
                      <Icone nome="cycle" tamanho={12} traco={1.7} /> Arraste uma pessoa para um
                      bloco (no celular, toque em “Trocar dono”): se ela já tem bloco aqui, os dois
                      trocam. Só blocos que ainda não começaram. Pede o motivo e fica registrado.
                    </>
                  ) : (
                    'Operação em rascunho: publique na Geração para liberar aos balconistas e poder trocar o dono.'
                  )}
                </p>
                <div className="gq-grade">
                  {blocos.map((b) => {
                    const pode = publicada && podeTransferirBloco(b);
                    const alvo = arrastando ? podeSoltarNoBloco(b, arrastando, blocos) : false;
                    return (
                      <article
                        key={b.numero}
                        className={`gq-bloco ${alvo ? 'gq-alvo' : ''} ${sobre === b.numero && alvo ? 'gq-sobre' : ''} ${arrastando && !alvo ? 'gq-apagado' : ''}`}
                        onDragOver={(e) => {
                          if (!alvo) return;
                          e.preventDefault();
                          setSobre(b.numero);
                        }}
                        onDragLeave={() => setSobre((x) => (x === b.numero ? null : x))}
                        onDrop={(e) => {
                          e.preventDefault();
                          soltar(b);
                        }}
                      >
                        <header>
                          <b>{bN(b.numero)}</b>
                          <span>{b.rotulo}</span>
                          <Selo etapa={b.etapa} />
                        </header>
                        <div
                          className="gq-dono"
                          draggable={publicada && !!b.responsavel}
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', b.responsavel?.accountId ?? '');
                            pegar(b.responsavel?.accountId);
                          }}
                          onDragEnd={() => {
                            setArrastando(null);
                            setSobre(null);
                          }}
                        >
                          {b.responsavel ? (
                            <>
                              <span className="gq-avatar">{iniciais(b.responsavel.nome)}</span>
                              <span className="gq-nome">{b.responsavel.nome}</span>
                            </>
                          ) : (
                            <span className="cv-cinza">Sem responsável</span>
                          )}
                        </div>
                        <Barra valor={b.contados} total={b.total} />
                        <footer>
                          <span>
                            {b.contados} de {b.total} contados
                            {b.aRepor > 0 &&
                              ` · ${String(b.repostos)}/${String(b.aRepor)} repostos`}
                          </span>
                          {pode && (
                            <button
                              type="button"
                              className="gr-link gr-link-botao"
                              onClick={() => setTransferindo({ bloco: b, para: '' })}
                            >
                              Trocar dono
                            </button>
                          )}
                        </footer>
                      </article>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="gq-sem">
                <p>
                  <b>O Giro de hoje deste setor ainda não foi gerado.</b> Os blocos nascem na
                  geração, com o dono escolhido pelo rodízio.
                </p>
                <div className="gq-acoes">
                  <button
                    type="button"
                    className="cv-botao"
                    disabled={simulando}
                    onClick={verPrevia}
                  >
                    {simulando ? 'Calculando…' : 'Ver prévia dos blocos (não grava nada)'}
                  </button>
                  <a className="cv-botao-primario" href="#/giro-geracao">
                    Ir para a Geração
                  </a>
                </div>
                {falhaPrevia && (
                  <p className="cv-falha" role="alert">
                    {falhaPrevia}
                  </p>
                )}
                {previa && (
                  <>
                    <p className="cv-cinza cv-peq gq-dica">
                      PRÉVIA · {previa.elegiveis} produtos das vendas de {diaCurto(previa.vendasDe)}{' '}
                      · quem o rodízio colocaria em cada bloco. Para trocar o dono: gere, publique e
                      arraste aqui.
                    </p>
                    <div className="gq-grade">
                      {previa.blocos.map((b) => (
                        <article key={b.numero} className="gq-bloco gq-previa">
                          <header>
                            <b>{bN(b.numero)}</b>
                            <span>{b.rotulo}</span>
                            <span className="gr-selo">Prévia</span>
                          </header>
                          <div className="gq-dono">
                            {b.responsavel ? (
                              <>
                                <span className="gq-avatar">{iniciais(b.responsavel.nome)}</span>
                                <span className="gq-nome">{b.responsavel.nome}</span>
                              </>
                            ) : (
                              <span className="cv-cinza">Sem responsável</span>
                            )}
                          </div>
                          <footer>
                            <span>{b.total} produtos</span>
                            {b.porque && <em title={b.porque}>{b.porque}</em>}
                          </footer>
                        </article>
                      ))}
                    </div>
                    {previa.semBloco.length > 0 && (
                      <p className="cv-cinza cv-peq">
                        Sem bloco hoje: {previa.semBloco.map((p) => p.nome).join(', ')}
                      </p>
                    )}
                    {previa.avisos.map((a) => (
                      <p key={a} className="cv-cinza cv-peq">
                        <Icone nome="warn" tamanho={12} traco={1.7} /> {a}
                      </p>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {transferindo && doSetor && (
        <Transferencia
          sessao={sessao}
          setor={doSetor}
          bloco={transferindo.bloco}
          dia={dia}
          paraInicial={transferindo.para}
          aoFechar={() => setTransferindo(null)}
          aoConcluir={() => {
            setTransferindo(null);
            carregar();
            lerPessoas();
          }}
        />
      )}
    </div>
  );
}
